import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  TextField,
} from "@mui/material";
import { Button, Dialog, Typography } from "@platform/design-system";

import { resolveTotalsBlockName } from "../../totalsBlockName";

import type {
  CustomTotalsBlock,
  DefaultTotalsBlock,
  FinancialsCoverageRef,
} from "@platform/api";

const createTotalsBlockSchema = (
  kind: "custom" | "default",
): z.ZodObject<{
  name: z.ZodString | z.ZodOptional<z.ZodString>;
  coverageIds: z.ZodArray<z.ZodString>;
}> =>
  z.object({
    name:
      kind === "custom"
        ? z
            .string()
            .trim()
            .min(1, "Block name is required")
            .max(140, "Block name must be 140 characters or less")
        : z.string().optional(),
    coverageIds: z.array(z.string()),
  });

interface TotalsBlockDialogProps {
  onClose: () => void;
  onSubmit: (name: string, coverageIds: string[]) => void;
  coverages: FinancialsCoverageRef[];
  existingBlock?: CustomTotalsBlock | DefaultTotalsBlock;
}

const CoverageCheckboxList = ({
  coverages,
  field,
}: {
  coverages: FinancialsCoverageRef[];
  field: { value: string[]; onChange: (value: string[]) => void };
}): React.JSX.Element => (
  <FormGroup>
    {coverages.map((coverage) => {
      const isChecked = field.value.includes(coverage.id);

      return (
        <FormControlLabel
          key={coverage.id}
          control={
            <Checkbox
              checked={isChecked}
              onChange={(_, checked) => {
                const next = checked
                  ? [...field.value, coverage.id]
                  : field.value.filter((id) => id !== coverage.id);
                field.onChange(next);
              }}
            />
          }
          label={coverage.name}
        />
      );
    })}
  </FormGroup>
);

export const TotalsBlockDialog = ({
  onClose,
  onSubmit,
  coverages,
  existingBlock,
}: TotalsBlockDialogProps): React.JSX.Element => {
  const isEditing = existingBlock !== undefined;
  const title = isEditing
    ? `Edit ${resolveTotalsBlockName(existingBlock)} Block`
    : "Add Totals Block";

  const schema = createTotalsBlockSchema(existingBlock?.kind ?? "custom");
  type TotalsBlockFormValues = z.infer<typeof schema>;

  const { control, handleSubmit, reset } = useForm<TotalsBlockFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: existingBlock?.name ?? "",
      coverageIds: existingBlock?.coverages.map((c) => c.coverageId) ?? [],
    },
  });

  const handleFormSubmit = (values: TotalsBlockFormValues): void => {
    onSubmit(values.name ?? "", values.coverageIds);
    reset();
    onClose();
  };

  const handleCancel = (): void => {
    reset();
    onClose();
  };

  return (
    <Dialog
      open
      onClose={handleCancel}
      title={title}
      testIdSuffix="totals-block"
      actions={
        <>
          <Button onClick={handleCancel} variant="tertiary">
            Cancel
          </Button>
          <Button onClick={handleSubmit(handleFormSubmit)} variant="primary">
            {isEditing ? "Save" : "Add"}
          </Button>
        </>
      }
    >
      <Controller
        name="name"
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            placeholder="Block Name"
            fullWidth
            error={Boolean(fieldState.error)}
            helperText={fieldState.error?.message}
            sx={{ mb: 2 }}
          />
        )}
      />
      <Typography sx={{ mb: 1 }}>
        <strong>Coverage Blocks to Roll Up:</strong>
      </Typography>
      <Controller
        name="coverageIds"
        control={control}
        render={({ field }) => (
          <CoverageCheckboxList coverages={coverages} field={field} />
        )}
      />
    </Dialog>
  );
};
